howspace.users = (() => {
    'use strict';

    /**
     * This will contain all the logics for handling web socket event
     **/

    let importingStarted;

    /**
     * Render import/preview progress in the notification bar.
     * @param {object} notification - The pubsub notification; object.progress is the percentage, or null when the total is unknown.
     * @param {string} prefix - Label shown before the percentage (e.g. "Preview users").
     * @param {string} [indeterminateMessage] - Message shown when progress is null/undefined; falls back to prefix.
     */
    const notifyProgress = (notification, prefix, indeterminateMessage) => {
        const rawProgress = notification.object.progress;
        // A null/undefined progress means the total row count is unknown (e.g. XLSX/ODS),
        // so show an indeterminate message with a spinner instead of a misleading percentage.
        // Markup mirrors the Spinner UI component (Real4\Ui\Component\Icon\Spinner).
        const spinner = ' <i class="icon primary rotate spinner-third" aria-hidden="true"><i class="fa-light fa-spinner-third"></i></i>';
        const message = (rawProgress === null || rawProgress === undefined)
            ? (indeterminateMessage || prefix) + spinner
            : prefix + ': ' + parseFloat(rawProgress).toFixed(1) + '%';
        let notificationBarContent = $('#real-notification-bar-content');
        if (notificationBarContent.is(':visible')) {
            notificationBarContent.html(message);
        } else {
            real.notification.show(message);
        }
    };

    /**
     * Notify user about the action that is being performed.
     * @param {string} action - The action that is being performed.
     * @returns {string} - The translated message for the action.
     */
    const getNotifyMessage = action => {
        const messageList = {
            'admin:userbatch:imported': howspace.translate('workspacesettings_users_importing_users'),
            'admin:userbatch:exported': howspace.translate('workspacesettings_users_exporting_users'),
            'admin:userbatch:deleted': howspace.translate('workspacesettings_users_deleting_users'),
            'admin:userbatch:restored': howspace.translate('workspacesettings_users_restoring_users'),
            'admin:userbatch:role-assigned': howspace.translate('workspacesettings_users_assigning_role_users'),
            'admin:userbatch:certified': howspace.translate('workspacesettings_users_certifying_users'),
            'admin:userbatch:decertified': howspace.translate('workspacesettings_users_decertifying_users'),
            'admin:userbatch:assessed': howspace.translate('workspacesettings_users_assessing_users'),
            'admin:userbatch:disapproved': howspace.translate('workspacesettings_users_disapproving_users'),
            'admin:userbatch:anonymized': howspace.translate('workspacesettings_users_anonymizing_users'),
        };

        return messageList[action] || howspace.translate('workspacesettings_users_importing_users');
    };

    /**
     * Shows deletion failures from the notification. Other failures fetch batch details and reset the import modal.
     *
     * @param {Object} notification - The notification object containing information about the failed user batch.
     * @param {Object} notification.object.userbatch - The user batch object with an `id` property.
     */
    const handleUserBatchFailed = (notification) => {
        if (notification.object.started_action === 'admin:userbatch:deleted') {
            real.notification.show(howspace.translate('workspacesettings_users_execute_batch_failed'), 'danger');
            return;
        }
        real.ajax.json(real.baseUri + '/api/userbatch/' + notification.object.userbatch.id, {
            type: 'GET',
            success: response => {
                const failedUserBatch = response.user_batch;
                const modal = $('#create-users-modal');
                const fileUploader = modal.find('#input-users-content .howspace-file-uploader');

                // Reset the file uploader
                fileUploader.find('.howspace-file-uploader-form').removeClass('d-none');
                fileUploader.find('.howspace-file-uploader-progress').addClass('d-none');

                // Re-enable the preview button
                modal.find('.modal-footer .preview-users-btn').prop('disabled', false);

                // Show the error message
                modal.find('.prepare-import-error').removeClass('d-none');
                failedUserBatch.error_keys.forEach(function (key) {
                    modal.find('.prepare-import-error li.' + key).removeClass('d-none');
                });
            }
        });
    }

    /**
     * Handles the preparation of a user batch import by fetching the user batch details,
     * showing the preview of users, and resetting the button state in the modal.
     */
    const handleUserBatchPrepareImport = () => {
        fetchUserBatch(() => {
            showPreviewUsers(() => {
                const modal = getCreateUsersModal();
                real.button.reset(
                    modal.find('.modal-footer .preview-users-btn'),
                    modal.find('#input-users-content')
                );
                real.button.reset(
                    modal.find('.modal-footer .generate-users-btn'),
                    modal.find('#input-users-content')
                );
            });
        });
    };

    /**
     * Prepares the data payload and notification message for starting a user batch operation.
     * Determines the correct status and translated message based on the user batch action type.
     *
     * @param {Object} notification - The notification object containing user batch details.
     * @returns {Object} - An object with `dataPayload` (for API request) and `startedText` (for UI notification).
     */
    const getUserBatchPrepareData = (notification) => {
        const dataPayload = {
            status: ''
        };

        let startedText = '';

        switch (notification.object.userbatch.action) {
            case 'import':
                dataPayload.status = 'importing';
                startedText = howspace.translate('workspacesettings_users_importing_users');
                break;
            case 'export':
                dataPayload.status = 'exporting';
                startedText = howspace.translate('workspacesettings_users_exporting_users');
                break;
            case 'delete':
                dataPayload.status = 'deleting';
                startedText = howspace.translate('workspacesettings_users_deleting_users');
                break;
            case 'restore':
                dataPayload.status = 'restoring';
                startedText = howspace.translate('workspacesettings_users_restoring_users');
                break;
            case 'assign_role':
                dataPayload.status = 'assigning_role';
                startedText = howspace.translate('workspacesettings_users_assigning_role_users');
                break;
            case 'certify':
                dataPayload.status = 'certifying';
                startedText = howspace.translate('workspacesettings_users_certifying_users');
                break;
            case 'decertify':
                dataPayload.status = 'decertifying';
                startedText = howspace.translate('workspacesettings_users_decertifying_users');
                break;
            case 'assess':
                dataPayload.status = 'assessing';
                startedText = howspace.translate('workspacesettings_users_assessing_users');
                break;
            case 'disapprove':
                dataPayload.status = 'disapproving';
                startedText = howspace.translate('workspacesettings_users_disapproving_users');
                break;
            case 'export_user_activity':
                dataPayload.status = 'exporting_user_activity';
                startedText = howspace.translate('workspacesettings_users_exporting_user_activity_users');
                break;
            case 'export_progress':
                dataPayload.status = 'exporting_progress';
                startedText = howspace.translate('workspacesettings_users_exporting_progress_users');
                break;
            case 'anonymize':
                dataPayload.status = 'anonymizing';
                startedText = howspace.translate('workspacesettings_users_anonymizing_users');
                break;
        }

        return {dataPayload, startedText};
    }

    /**
     * Returns a localized completion message based on the status of a user batch operation.
     *
     * @param {Object} userbatch - The user batch object containing the operation status.
     * @param {string} userbatch.status - The status of the batch operation (e.g., 'imported', 'deleted').
     * @returns {string} The localized message indicating the result of the batch operation.
     */
    const getUserBatchCompletedMessage = (userbatch) => {
        if (!userbatch || !userbatch.status) {
            return howspace.translate('workspacesettings_users_execute_batch_failed');
        }

        if (userbatch.status === 'deleted' && userbatch.number_of_users_for_no_change > 0) {
            const keptText = howspace.translate('workspacesettings_users_delete_self_kept_info');
            return userbatch.number_of_users_updated > 0
                ? howspace.translate('workspacesettings_users_user_delete_completed') + ' ' + keptText
                : keptText;
        }

        const messageList = {
            'imported': howspace.translate('workspacesettings_users_user_import_completed'),
            'exported': howspace.translate('workspacesettings_users_user_export_completed'),
            'deleted': howspace.translate('workspacesettings_users_user_delete_completed'),
            'restored': howspace.translate('workspacesettings_users_user_restore_completed'),
            'role_assigned': howspace.translate('workspacesettings_users_user_role_assign_completed'),
            'certified': howspace.translate('workspacesettings_users_user_certify_completed'),
            'decertified': howspace.translate('workspacesettings_users_user_decertify_completed'),
            'assessed': howspace.translate('workspacesettings_users_user_assess_completed'),
            'disapproved': howspace.translate('workspacesettings_users_user_disapprove_completed'),
            'anonymized': howspace.translate('workspacesettings_users_user_anonymize_completed'),
            'exported_user_activity': howspace.translate('workspacesettings_users_user_activity_export_completed'),
            'exported_progress': howspace.translate('workspacesettings_users_export_progress_completed'),
        };

        return messageList[userbatch.status] || howspace.translate('workspacesettings_users_execute_batch_failed');
    };


    /**
     * Returns a translated message describing the type of user update performed, based on the user batch status.
     *
     * @param {Object} userbatch - The user batch object containing a `status` property.
     * @returns {string} The translated message for the given status, or an empty string if not recognized.
     */
    const getUsersUpdatedText = (userbatch) => {
        if (!userbatch || !userbatch.status) {
            return '';
        }

        const status = userbatch.status;

        // Priority: unexpected errors > created & updated > created > updated > status
        if (userbatch.number_of_unexpected_errors_during_import > 0) {
            return howspace.translate('workspacesettings_users_unexpectederror_users');
        }
        if (userbatch.number_of_users_created > 0 && userbatch.number_of_users_updated > 0) {
            return howspace.translate('workspacesettings_users_created_and_updated_users');
        }
        if (userbatch.number_of_users_created > 0) {
            return howspace.translate('workspacesettings_users_created_users');
        }
        if (userbatch.number_of_users_updated > 0) {
            if (status === 'imported') {
                return howspace.translate('workspacesettings_users_updated_users');
            } else if (status === 'deleted') {
                return howspace.translate('workspacesettings_users_deleted_users');
            } else if (status === 'restored') {
                return howspace.translate('workspacesettings_users_restored_users');
            } else if (status === 'role_assigned') {
                return howspace.translate('workspacesettings_users_role_assigned_users');
            } else if (status === 'assessed') {
                return howspace.translate('workspacesettings_users_assessed_users');
            } else if (status === 'disapproved') {
                return howspace.translate('workspacesettings_users_disapproved_users');
            } else if (status === 'certified') {
                return howspace.translate('workspacesettings_users_certified_users');
            } else if (status === 'decertified') {
                return howspace.translate('workspacesettings_users_decertified_users');
            } else if (status === 'anonymized') {
                return howspace.translate('workspacesettings_users_anonymized_users');
            } else {
                return howspace.translate('workspacesettings_users_updated_users');
            }
        }

        return '';
    }

    /**
     * Builds the information (buttons and text) to display when a user batch operation is completed.
     * Generates action buttons for viewing created/updated users, downloading files, or showing errors,
     * depending on the batch status and result counts.
     *
     * @param {Object} userbatch - The user batch object containing status and result counts.
     * @param {Object} notification - The notification object containing additional details (e.g., uploaded file).
     * @returns {Object} An object with `buttons` (array of HTML strings) and `usersText` (localized text).
     */
    const buildUserBatchCompletedInfo = (userbatch, notification) => {
        let buttons = [];
        let usersText = getUsersUpdatedText(userbatch);

        if (userbatch.number_of_users_updated > 0) {
            const statusesWithDownloadOnly = ['exported', 'exported_user_activity', 'exported_progress'];
            if (statusesWithDownloadOnly.includes(userbatch.status)) {
                buttons.push(
                    `<a data-type="updated" href="/uploadedfile/${notification.object.userbatch.uploadedfile.id}/view">
                        ${notification.object.userbatch.uploadedfile.filename}
                    </a>`
                );
            }

            const hideUsersUpdatedStatuses = [...statusesWithDownloadOnly, 'anonymized'];
            if (!hideUsersUpdatedStatuses.includes(userbatch.status)) {
                buttons.push(`
                    <button data-type="updated" data-show-deleted="${userbatch.status === 'deleted' ? 1 : 0}" class="btn btn-tertiary show-users-btn">
                        ${usersText} (${userbatch.number_of_users_updated})
                    </button>
                `);
            }

            // If errors occurred
            if (userbatch.number_of_unexpected_errors_during_import > 0) {
                buttons.push(`
                    <button data-type="unexpectederror" data-show-deleted="0" class="btn btn-tertiary show-users-btn">
                        ${usersText} (${userbatch.number_of_unexpected_errors_during_import})
                    </button>
                `);
            }
        }

        return {buttons, usersText};
    }

    /**
     * Displays a notification or message based on the user batch status and the provided notification.
     * If a message is present in the notification, it shows that; otherwise, it constructs a message
     * based on the user batch status and the provided buttons.
     * @param {Object} userbatch - The user batch object containing status and result counts.
     * @param {string} completedText - The text to display when the user batch is completed.
     * @param {Array} buttons - An array of HTML strings representing action buttons.
     * @param {Object} notification - The notification object containing additional details (e.g., message, level).
     */
    const showNotificationOrMessage = (userbatch, completedText, buttons, notification) => {
        const message = notification.object.message || '';
        const level = notification.object.level || 'success';

        if (message) {
            real.notification.show(message, level);
        } else if (userbatch.status !== 'imported') {
            real.notification.show(`${completedText}<span class="ml-3">${buttons.join('<span class="text-muted mx-2">•</span>')}</span>`, level);
        }
    };

    /**
     * Determines the business rule for a given user batch based on its properties.
     *
     * @param {Object} userbatch - The user batch object containing import and user creation/update information.
     * @param {number} userbatch.id - The unique identifier of the user batch.
     * @param {number} userbatch.number_of_unexpected_errors_during_import - The number of unexpected errors during import.
     * @param {number} userbatch.number_of_users_created - The number of users created in the batch.
     * @param {number} userbatch.number_of_users_updated - The number of users updated in the batch.
     * @returns {Object} An object containing the user batch ID and the determined business rule.
     */
    const getBusinessRule = userbatch => {
        const businessRule = {
            'userbatch': {
                'oid': userbatch.id,
            },
            'rule': ''
        };

        if (userbatch.number_of_unexpected_errors_during_import > 0) {
            businessRule.rule = 'userbatch_unexpectederror_users';
        } else if (userbatch.number_of_users_created > 0 && userbatch.number_of_users_updated > 0) {
            businessRule.rule = 'userbatch_created_and_updated_users';
        } else if (userbatch.number_of_users_created > 0) {
            businessRule.rule = 'userbatch_created_users';
        } else if (userbatch.number_of_users_updated > 0 && userbatch.status !== 'deleted') {
            businessRule.rule = 'userbatch_updated_users';
        }

        // When creating demo users, nullify the rule.
        if ((userbatch.number_of_demo_users > 0) && (userbatch.number_of_users_created === userbatch.number_of_demo_users)) {
            businessRule.rule = null;
        }

        return businessRule;
    }

    /**
     * Sets up the click event handler for the "Show Users" button in the notification bar
     * @param {Object} userbatch - The user batch object containing status and result counts.
     * @param {string} usersText - The text to display for the users.
     */
    const setupShowUsersButton = (userbatch, usersText) => {
        $('#real-notification-bar-content').on('click', '.show-users-btn', (e) => {
            const btn = $(e.currentTarget);
            const type = btn.data('type');
            const showDeleted = btn.data('show-deleted');
            const url = '/userbatch/' + userbatch.id + '/' + type + '-users?show_deleted=' + showDeleted;
            const businessRule = getBusinessRule(userbatch);

            howspace.components.modal.showUsers(
                url,
                usersText,
                'first_name,last_name,email',
                'asc',
                businessRule,
                {},
                (newModal, modalOpener) => {
                    real.notification.hide();
                }
            );
        });
    };

    /**
     * Displays a modal with the list of imported users after a user batch operation is completed.
     * It constructs the URL for the modal and sets up a business rule for filtering the users.
     * @param {Object} userbatch - The user batch object containing status and result counts.
     * @param {string} usersText - The text to display for the users.
     */
    const showImportedUsersModal = (userbatch, usersText) => {
        const url = `/userbatch/${userbatch.id}/imported-users`;
        const businessRule = getBusinessRule(userbatch);
        howspace.components.modal.showUsers(url, usersText, 'first_name,last_name,email', 'asc', businessRule, {}, () => {
            real.notification.hide();
        });
    };

    /**
     * Batch statuses whose batch leaves alone the people a shared group keeps, mapped to the action the skipped
     * modal words its text for.
     */
    const SHARED_GROUP_SKIPPING_ACTIONS = {
        'deleted': 'delete',
        'anonymized': 'anonymize',
    };

    /**
     * Says how many people a batch left alone because a shared group keeps them, in a modal the admin has to close.
     * A notification would be replaced by the batch's own result before it could be read. A modal the admin is
     * working in is left open, and this one follows when it closes.
     *
     * When the skip is the batch's whole result, the notification is this modal's too. It carries the same text as a
     * warning while the modal waits, so it is said at once, and goes when the modal opens.
     *
     * @param {string} action - The batch action, 'delete' or 'anonymize'.
     * @param {number} count - How many people were left alone.
     * @param {boolean} isWholeResult - True when the batch changed nobody and nothing failed.
     */
    const showSharedGroupSkippedModal = (action, count, isWholeResult) => {
        // Asked again on every close, because a modal can close by opening the next one.
        const openModal = document.querySelector('.modal:not(.d-none, [closing])');
        if (openModal) {
            if (isWholeResult) {
                real.notification.show(getSharedGroupSkippedText(action, count), 'warning');
            }
            openModal.addEventListener(
                howspace.components.modal.MODAL_CLOSED,
                () => showSharedGroupSkippedModal(action, count, isWholeResult),
                {once: true}
            );
            return;
        }

        if (isWholeResult) {
            real.notification.hide();
        }
        howspace.components.modal.show(
            `${real.baseUri}/manager/users/shared-group-skipped-modal?action=${action}&count=${count}`
        );
    };

    /**
     * The skipped modal's text, for the notification shown while the modal waits.
     *
     * @param {string} action - The batch action, 'delete' or 'anonymize'.
     * @param {number} count - How many people were left alone.
     * @returns {string}
     */
    const getSharedGroupSkippedText = (action, count) => action === 'anonymize'
        ? howspace.translate('workspacesettings_users_skipped_shared_group_members_anonymize', {count: count})
        : howspace.translate('workspacesettings_users_skipped_shared_group_members_delete', {count: count});

    /**
     * Handles the completion of a user batch operation by fetching the user batch details,
     * displaying a notification with the completion message, and setting up the "Show Users" button.
     * It also ensures that the "importing users" notification remains visible for at least 1 second.
     * @param {Object} notification - The notification object containing user batch details.
     */
    const userBatchCompleted = notification => {
        real.ajax.json(notification.object.userbatch.href, {
            type: 'GET',
            success: response => {
                const userbatch = response.user_batch;

                // Keep "importing users" notification visible for at least 1 second
                let timeoutDuration = Math.max(0, 1000 - (Date.now() - importingStarted));

                setTimeout(() => {
                    const skippingAction = SHARED_GROUP_SKIPPING_ACTIONS[userbatch.status];
                    if (skippingAction && userbatch.number_of_users_skipped > 0) {
                        // With nobody changed, nobody kept as the last admin and nothing failed, the modal is the
                        // whole result. Saying the batch completed would read as if the people it kept had been
                        // removed after all.
                        const isWholeResult = userbatch.number_of_users_updated === 0
                            && !(userbatch.number_of_users_for_no_change > 0)
                            && !(userbatch.number_of_unexpected_errors_during_import > 0);
                        showSharedGroupSkippedModal(skippingAction, userbatch.number_of_users_skipped, isWholeResult);
                        if (isWholeResult) {
                            return;
                        }
                    }

                    let completedText = getUserBatchCompletedMessage(userbatch);
                    const {buttons, usersText} = buildUserBatchCompletedInfo(userbatch, notification);

                    showNotificationOrMessage(userbatch, completedText, buttons, notification);
                    setupShowUsersButton(userbatch, usersText);

                    if (userbatch.status === 'imported') {
                        showImportedUsersModal(userbatch, usersText);
                    }
                }, timeoutDuration);
            }
        });
    };

    real.onTierReady(real.TIER_2, () => {
        real.events.on('admin:userbatch:prepare:progress', (notification) => {
            notifyProgress(
                notification,
                howspace.translate('workspacesettings_users_preview_users'),
                howspace.translate('workspacesettings_users_generating_preview')
            );
        });
        real.events.on('admin:userbatch:execute:progress', (notification) => {
            const message = getNotifyMessage(notification.object?.started_action);
            notifyProgress(notification, message);
        });

        real.events.on('admin:userbatch:failed', notification => {
            handleUserBatchFailed(notification);
        });

        real.events.on('admin:userbatch:prepared', notification => {
            real.notification.hide();

            const modal = getCreateUsersModal();
            if (modal) {
                modal.data('userbatch-id', notification.object.userbatch.id);
                modal.data('userbatch-api-url', real.baseUri + '/api/userbatch/' + notification.object.userbatch.id);
                modal.data('userbatch-ui-url', real.baseUri + '/userbatch/' + notification.object.userbatch.id);
            }

            if (notification.object.userbatch.action === 'import') {
                handleUserBatchPrepareImport();
            } else {
                const {dataPayload, startedText} = getUserBatchPrepareData(notification);

                real.notification.show(startedText + '...', null, false);

                real.ajax.json(real.baseUri + '/api/userbatch/' + notification.object.userbatch.id, {
                    type: 'POST',
                    data: JSON.stringify(dataPayload),
                    success: () => {
                        importingStarted = Date.now();
                    }
                });
            }
        });

        real.events.on('admin:userbatch:imported', userBatchCompleted);
        real.events.on('admin:userbatch:exported', userBatchCompleted);
        real.events.on('admin:userbatch:deleted', userBatchCompleted);
        real.events.on('admin:userbatch:restored', userBatchCompleted);
        real.events.on('admin:userbatch:role-assigned', userBatchCompleted);
        real.events.on('admin:userbatch:certified', userBatchCompleted);
        real.events.on('admin:userbatch:decertified', userBatchCompleted);
        real.events.on('admin:userbatch:assessed', userBatchCompleted);
        real.events.on('admin:userbatch:disapproved', userBatchCompleted);
        real.events.on('admin:userbatch:anonymized', userBatchCompleted);
        real.events.on('admin:userbatch:exported-user-activity', userBatchCompleted);
        real.events.on('admin:userbatch:exported-progress', userBatchCompleted);
    });

    const fetchUserBatch = (readyFn) => {
        const modal = getCreateUsersModal();
        real.ajax.json(modal.data('userbatch-api-url'), {
            type: 'GET',
            success: function (response) {
                const userbatch = response.user_batch;

                modal.find('#preview-users-content').data('user-batch', JSON.stringify(userbatch));

                updateUserCounts(userbatch);

                const urlPrefix = real.baseUri + '/users';
                const urlParams = 'user_batch_id=' + userbatch.id;

                const invalidUsersLister = modal.find('#invalid-users .howspace-lister');
                const usersToBeCreatedLister = modal.find('#users-to-be-created .howspace-lister');
                const usersToBeUpdatedLister = modal.find('#users-to-be-updated .howspace-lister');
                const usersForNoChangeLister = modal.find('#users-for-no-change .howspace-lister');

                if (userbatch.number_of_invalid_users === 0) {
                    modal.find('#invalid-users').addClass('d-none');
                } else {
                    modal.find('#invalid-users').removeClass('d-none');
                    invalidUsersLister.dataAndAttr('uri', urlPrefix + '/invalid-users/?' + urlParams);
                    howspace.components.lister.reload(invalidUsersLister);
                }

                if (userbatch.number_of_users_to_be_created === 0) {
                    modal.find('#users-to-be-created').addClass('d-none');
                } else {
                    modal.find('#users-to-be-created').removeClass('d-none');
                    usersToBeCreatedLister.data('uri', urlPrefix + '/users-to-be-created/?' + urlParams);
                    howspace.components.lister.reload(usersToBeCreatedLister);
                }

                if (userbatch.number_of_users_to_be_updated === 0) {
                    modal.find('#users-to-be-updated').addClass('d-none');
                } else {
                    modal.find('#users-to-be-updated').removeClass('d-none');
                    usersToBeUpdatedLister.data('uri', urlPrefix + '/users-to-be-updated/?' + urlParams);
                    howspace.components.lister.reload(usersToBeUpdatedLister);
                }

                if (userbatch.number_of_users_for_no_change === 0) {
                    modal.find('#users-for-no-change').addClass('d-none');
                } else {
                    modal.find('#users-for-no-change').removeClass('d-none');
                    usersForNoChangeLister.data('uri', urlPrefix + '/users-for-no-change/?' + urlParams);
                    howspace.components.lister.reload(usersForNoChangeLister);
                }

                if (
                    userbatch.number_of_invalid_users === 0
                    && userbatch.number_of_users_to_be_created === 0
                    && userbatch.number_of_users_to_be_updated === 0
                    && userbatch.number_of_users_for_no_change === 0
                ) {
                    modal.find('#preview-users-content .empty-list').removeClass('d-none');
                } else {
                    modal.find('#preview-users-content .empty-list').addClass('d-none');
                }

                if (typeof readyFn === 'function') {
                    readyFn();
                }
            }
        });
    };

    const updateCreateUsersModalUser = function (updatedUser, tr) {
        const userbatch = JSON.parse($('#create-users-modal #preview-users-content').data('user-batch'));

        real.ajax.html(real.baseUri + '/users/user-state?user_id=' + updatedUser.id, {
            type: 'GET',
            success: function (response) {
                /* Do not update the whole row, only the parent td element of the input. */
                tr.find('.choose-import-container').replaceWith($(response).find('.choose-import-container'));
                updateUserCounts(userbatch);
            }
        });
    };

    /**
     * Returns the modal element for creating users.
     * It checks if the modal with ID 'create-users-modal' exists, otherwise returns 'create-ai-users-modal'.
     */
    function getCreateUsersModal() {
        const importModal = $('#create-users-modal');
        if (importModal.length) {
            return importModal;
        }
        const aiGenModal = $('#create-ai-users-modal');
        if (aiGenModal.length) {
            return aiGenModal;
        }
        return null;
    }

    const updateUserCounts = (userbatch) => {
        if (!userbatch) {
            return;
        }

        const modal = getCreateUsersModal();

        modal.find('#invalid-users .user-count').text(
            howspace.translate('workspacesettings_users_invalid_users') + ' (' + userbatch.number_of_invalid_users + ')');
        modal.find('#users-to-be-created .user-count').text(
            howspace.translate('workspacesettings_users_new_users_to_be_created') + ' (' + userbatch.number_of_users_to_be_created + ')');
        modal.find('#users-to-be-updated .user-count').text(
            howspace.translate('workspacesettings_users_users_to_be_updated') + ' (' + userbatch.number_of_users_to_be_updated + ')');
        modal.find('#users-for-no-change .user-count').text(
            howspace.translate('workspacesettings_users_users_for_no_change') + ' (' + userbatch.number_of_users_for_no_change + ')');

        if (userbatch.number_of_users_to_be_created === 0 && userbatch.number_of_users_to_be_updated === 0) {
            modal.find('.start-importing-users-btn').prop('disabled', true);
        } else {
            modal.find('.start-importing-users-btn').prop('disabled', false);
        }
    };

    const showPreviewUsers = function (readyFn) {
        const modal = getCreateUsersModal();
        const aniDuration = howspace.getAnimationDuration(modal, '--howspace-animation-200');

        modal.find('.cancel-btn').addClass('d-none');
        modal.find('.preview-users-btn').addClass('d-none');
        modal.find('.generate-users-btn').addClass('d-none');
        modal.find('.input-users-btn').removeClass('d-none');
        modal.find('.start-importing-users-btn').removeClass('d-none');

        const visibleContent = modal.find('.process-content:visible');
        const hiddenContent = modal.find('.process-content:hidden');

        howspace.components.autosize.init(hiddenContent);

        howspace.animate(visibleContent, 'slideUp', aniDuration, () => {
            visibleContent.addClass('d-none');
            howspace.animate(hiddenContent, 'slideDown', aniDuration, () => {
                hiddenContent.removeClass('d-none');
                real.input.focus(hiddenContent.find('input').first());
                howspace.components.autosize.destroy(visibleContent);
                if (typeof readyFn === 'function') {
                    readyFn();
                }
            });
        });
    };

    const showInputUsers = function (_readyFn) {
        const modal = getCreateUsersModal();
        const aniDuration = howspace.getAnimationDuration(modal, '--howspace-animation-200');

        modal.find('.cancel-btn').removeClass('d-none');
        modal.find('.preview-users-btn').removeClass('d-none');
        modal.find('.generate-users-btn').removeClass('d-none');
        modal.find('.input-users-btn').addClass('d-none');
        modal.find('.start-importing-users-btn').addClass('d-none');

        const visibleContent = modal.find('.process-content:visible');
        const hiddenContent = modal.find('.process-content:hidden');
        howspace.components.autosize.init(hiddenContent);

        howspace.animate(visibleContent, 'slideUp', aniDuration, () => {
            visibleContent.addClass('d-none');
            // Reset preview users.
            modal.find('#invalid-users tbody').html('');
            modal.find('#users-to-be-created tbody').html('');
            modal.find('#users-to-be-updated tbody').html('');
            modal.find('#users-for-no-change tbody').html('');

            modal.find('.howspace-file-uploader-form').removeClass('d-none');
            modal.find('.howspace-file-uploader-progress').addClass('d-none').html('');

            howspace.animate(hiddenContent, 'slideDown', aniDuration, () => {
                hiddenContent.removeClass('d-none');
                real.input.focus(hiddenContent.find('input').first());
                howspace.components.autosize.destroy(visibleContent);
            });
        });
    };

    const initStartImportingUsersBtn = function (modal) {
        modal.find('.start-importing-users-btn').on('click', function (_e) {
            const btn = $(this);
            real.button.loading(btn, modal.find('#preview-users-content'));

            const dataPayload = {
                status: 'importing'
            };

            real.ajax.json(modal.data('userbatch-api-url'), {
                type: 'POST',
                data: JSON.stringify(dataPayload),
                success: function (_response) {
                    howspace.components.modal.close(modal[0]);
                },
                error: function (response) {
                    console.log('Error:', response);
                }
            });
        });
    };

    const initAddUserModal = (modal) => {
        modal = $(modal);

        modal.find('.modal-footer .input-users-btn').addClass('d-none');
        modal.find('.modal-footer .start-importing-users-btn').addClass('d-none');

        real.input.focus(modal.find('.rawdata-textarea'));

        modal.find('.modal-footer .input-users-btn').on('click', function (_e) {
            showInputUsers();
        });

        modal.find('#input-users-content').on('change', '.howspace-file-input', () => {
            modal.find('.modal-footer .preview-users-btn').prop('disabled', true);
        });

        modal.find('.modal-footer .preview-users-btn').on('click', (e) => {
            const btn = $(e.target);
            real.button.loading(btn, modal.find('#input-users-content'));
            const dataPayload = real.form.getDataPayload(modal.find('#input-users-content'));
            real.ajax.json(real.baseUri + '/api/userbatches', {
                type: 'POST',
                data: JSON.stringify(dataPayload),
                logError: (response) => {
                    let logError = true;
                    real.ajax.loopErrors(response, (error) => {
                        if (error.message.startsWith('Too many users for text import')) {
                            logError = false;
                        }
                    });
                    return logError;
                },
                error: () => {
                    real.button.reset(btn, modal.find('#input-users-content'));
                }
            });
        });

        initStartImportingUsersBtn(modal);

        modal.on('change', '.choose-import-checkbox', function (_e) {
            const checkbox = $(this);
            const userbatch = JSON.parse($('#create-users-modal #preview-users-content').data('user-batch'));
            const userRow = checkbox.closest('.user-row');
            const userId = userRow.data('user-id');
            const parent = userRow.closest('.userlist-collapse');

            if (checkbox.prop('checked')) {
                const dataPayload = {
                    deleted_at: null
                };
                real.ajax.json(real.baseUri + '/api/batchuser/' + userId, {
                    type: 'POST',
                    data: JSON.stringify(dataPayload),
                    success: function (response) {
                        if (parent.is('#users-to-be-created')) {
                            userbatch.number_of_users_to_be_created = userbatch.number_of_users_to_be_created + 1;
                        } else if (parent.is('#users-to-be-updated')) {
                            userbatch.number_of_users_to_be_updated = userbatch.number_of_users_to_be_updated + 1;
                        }

                        $('#create-users-modal #preview-users-content').data('user-batch', JSON.stringify(userbatch));

                        updateCreateUsersModalUser(response.user, checkbox.closest('tr'));
                    }
                });
            } else {
                real.ajax.json(real.baseUri + '/api/batchuser/' + userId, {
                    type: 'DELETE',
                    success: function (response) {
                        if (parent.is('#users-to-be-created')) {
                            userbatch.number_of_users_to_be_created = userbatch.number_of_users_to_be_created - 1;
                        } else if (parent.is('#users-to-be-updated')) {
                            userbatch.number_of_users_to_be_updated = userbatch.number_of_users_to_be_updated - 1;
                        }

                        $('#create-users-modal #preview-users-content').data('user-batch', JSON.stringify(userbatch));

                        updateCreateUsersModalUser(response.user, checkbox.closest('tr'));
                    }
                });
            }
        });

        modal.on('change', '.user-name-input', (e) => {
            const input = $(e.target);
            const originalValue = input.data('original-value');

            if (originalValue && originalValue.trim() === input.val().trim()) {
                return;
            }

            const row = input.closest('tr');

            if (!row.data('user-id')) {
                return;
            }

            real.button.loading(input, row);

            const dataPayload = real.form.getDataPayload(row);

            real.ajax.json(real.baseUri + '/api/batchuser/' + row.data('user-id'), {
                type: 'POST',
                data: JSON.stringify(dataPayload),
                success: response => {
                    real.button.reset(input, row);
                    updateCreateUsersModalUser(response.user, row);
                }
            });
        });

        modal.on('click', '.role-select-option-btn', (e) => {
            const btn = $(e.currentTarget);
            const dropdown = btn.closest('.role-select-dropdown');
            const input = dropdown.closest('td').find('.role-select');
            const roleId = String(btn.data('role-id'));

            howspace.components.dropdown.close(dropdown);

            if (String(input.val()) === roleId) {
                return;
            }

            dropdown.find('.role-select-label').text(btn.data('role-label'));
            dropdown.find('.role-select-option-btn').removeClass('active');
            btn.addClass('active');
            input.val(roleId).trigger('change');
        });

        modal.on('change', '.role-checkbox, .role-select', (e) => {
            const input = $(e.target);
            const row = input.closest('tr');

            if (!row.data('user-id')) {
                return;
            }

            real.button.loading(input, row);

            const dataPayload = real.form.getDataPayload(row);

            real.ajax.json(real.baseUri + '/api/batchuser/' + row.data('user-id'), {
                type: 'POST',
                data: JSON.stringify(dataPayload),
                success: response => {
                    real.button.reset(input, row);
                    updateCreateUsersModalUser(response.user, row);
                }
            });
        });
    };

    const initAiUserCreationModal = (modal) => {
        modal = $(modal);

        modal.find('.modal-footer .start-importing-users-btn').addClass('d-none');
        modal.find('.input-users-btn').addClass('d-none');

        modal.find('.modal-footer .input-users-btn').on('click', function (_e) {
            showInputUsers();
        });

        modal.find('.generate-users-btn').on('click', function (_e) {
            const btn = $(this);
            real.button.loading(btn);

            const dataPayload = real.form.getDataPayload(modal);

            real.ajax.json(real.baseUri + '/api/ai/prompt/generate-users', {
                type: 'POST',
                data: JSON.stringify(dataPayload),
                success: function (_response) {
                    console.log('AI user generation started successfully.');
                },
                error: function (response) {
                    console.log('Error:', response);
                }
            });
        });

        initStartImportingUsersBtn(modal);
    }

    return {
        initAddUserModal: initAddUserModal,
        initAiUserCreationModal: initAiUserCreationModal,
    };

})();
